import React, { useState } from 'react';
import { entriesApi, statementApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Upload, FileSpreadsheet, Check, AlertCircle, Loader2, Sparkles, BookOpen } from 'lucide-react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { CATEGORIES, SUB_CAT_CATEGORIES, getEntityTypeForCategory } from '@/constants/categories';

// Bank format detection patterns
const BANK_FORMATS = {
  sbi: {
    name: 'SBI',
    dateCol: ['Txn Date', 'txn date', 'Value Date', 'Transaction Date'],
    debitCol: ['Debit', 'Withdrawal Amt.', 'Withdrawal', 'Withdrawal Amt'],
    creditCol: ['Credit', 'Deposit Amt.', 'Deposit', 'Deposit Amt'],
    descCol: ['Description', 'Narration', 'Particulars', 'Ref No./Cheque No.'],
  },
  hdfc: {
    name: 'HDFC',
    dateCol: ['Date', 'Value Date', 'Txn Date'],
    debitCol: ['Withdrawal Amt.', 'Debit', 'Withdrawal Amount', 'Debit Amount'],
    creditCol: ['Deposit Amt.', 'Credit', 'Deposit Amount', 'Credit Amount'],
    descCol: ['Narration', 'Description', 'Particulars', 'Transaction Remarks'],
  },
  icici: {
    name: 'ICICI',
    dateCol: ['Transaction Date', 'Value Date', 'Date', 'Txn Date'],
    debitCol: ['Withdrawal Amount (INR)', 'Withdrawal Amount', 'Debit', 'DR'],
    creditCol: ['Deposit Amount (INR)', 'Deposit Amount', 'Credit', 'CR'],
    descCol: ['Transaction Remarks', 'Particulars', 'Description', 'Narration'],
  },
  axis: {
    name: 'Axis',
    dateCol: ['Tran Date', 'Transaction Date', 'Date'],
    debitCol: ['DR', 'Debit', 'Withdrawal'],
    creditCol: ['CR', 'Credit', 'Deposit'],
    descCol: ['PARTICULARS', 'Description', 'Narration'],
  },
  generic: {
    name: 'Generic',
    dateCol: ['Date', 'Transaction Date', 'Txn Date', 'Value Date', 'Tran Date'],
    debitCol: ['Debit', 'Withdrawal', 'DR', 'Withdrawal Amt', 'Debit Amount', 'Amount'],
    creditCol: ['Credit', 'Deposit', 'CR', 'Deposit Amt', 'Credit Amount'],
    descCol: ['Description', 'Particulars', 'Narration', 'Remarks', 'Details', 'Transaction Remarks'],
  }
};

function findMatchingCol(headers, candidates) {
  const lowerHeaders = headers.map(h => h.toLowerCase().trim());
  for (const candidate of candidates) {
    const idx = lowerHeaders.indexOf(candidate.toLowerCase().trim());
    if (idx !== -1) return headers[idx];
  }
  return '';
}

function detectBankFormat(headers) {
  for (const [key, fmt] of Object.entries(BANK_FORMATS)) {
    const dateMatch = findMatchingCol(headers, fmt.dateCol);
    const debitMatch = findMatchingCol(headers, fmt.debitCol);
    const descMatch = findMatchingCol(headers, fmt.descCol);
    if (dateMatch && (debitMatch || descMatch)) {
      return { key, name: fmt.name, dateCol: dateMatch, debitCol: debitMatch, creditCol: findMatchingCol(headers, fmt.creditCol), descCol: descMatch };
    }
  }
  return null;
}

function parseDate(dateStr) {
  if (!dateStr) return '';
  const s = String(dateStr).trim();
  // Try DD/MM/YYYY or DD-MM-YYYY
  const dmyMatch = s.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})$/);
  if (dmyMatch) return `${dmyMatch[3]}-${dmyMatch[2].padStart(2, '0')}-${dmyMatch[1].padStart(2, '0')}`;
  // Try YYYY-MM-DD
  const ymdMatch = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (ymdMatch) return `${ymdMatch[1]}-${ymdMatch[2].padStart(2, '0')}-${ymdMatch[3].padStart(2, '0')}`;
  // Try DD MMM YYYY
  const monthMap = { jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06', jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12' };
  const dMyMatch = s.match(/^(\d{1,2})\s+(\w{3})\s+(\d{4})$/);
  if (dMyMatch && monthMap[dMyMatch[2].toLowerCase()]) {
    return `${dMyMatch[3]}-${monthMap[dMyMatch[2].toLowerCase()]}-${dMyMatch[1].padStart(2, '0')}`;
  }
  // Try Excel serial number (number > 30000)
  const num = parseFloat(s);
  if (!isNaN(num) && num > 30000 && num < 80000) {
    const d = new Date((num - 25569) * 86400 * 1000);
    return d.toISOString().slice(0, 10);
  }
  return s;
}

function parseAmount(val) {
  if (!val) return 0;
  const cleaned = String(val).replace(/[,\s]/g, '').replace(/[()]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : Math.abs(num);
}

export function BankStatementDialog({ open, onOpenChange, sites, entities, entityBankAccounts, fetchEntityBankAccounts, onSuccess }) {
  const [step, setStep] = useState('upload'); // upload, map, review
  const [rawData, setRawData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [detectedFormat, setDetectedFormat] = useState(null);
  const [fileType, setFileType] = useState('');
  const [parsing, setParsing] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  // Column mapping
  const [dateCol, setDateCol] = useState('');
  const [debitCol, setDebitCol] = useState('');
  const [creditCol, setCreditCol] = useState('');
  const [descCol, setDescCol] = useState('');

  // Common fields
  const [siteId, setSiteId] = useState('');
  const [category, setCategory] = useState('material');
  const [subCategory, setSubCategory] = useState('');
  const [bulkEntityId, setBulkEntityId] = useState('');
  const [bulkEntityName, setBulkEntityName] = useState('');
  const [bulkBankAccountId, setBulkBankAccountId] = useState('');
  const [bulkBankAccountName, setBulkBankAccountName] = useState('');

  // Parsed rows
  const [parsedRows, setParsedRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const catEntities = entities.filter(e => {
    const t = getEntityTypeForCategory(category);
    return t ? e.entity_type === t : true;
  });

  const processHeadersAndData = (hdrs, data) => {
    setHeaders(hdrs);
    setRawData(data);
    const detected = detectBankFormat(hdrs);
    if (detected) {
      setDetectedFormat(detected);
      setDateCol(detected.dateCol);
      setDebitCol(detected.debitCol);
      setCreditCol(detected.creditCol);
      setDescCol(detected.descCol);
    }
    setStep('map');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    setFileType(ext);
    setParsing(true);

    try {
      if (ext === 'csv') {
        // CSV parsing with PapaParse
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            processHeadersAndData(results.meta.fields || [], results.data);
            setParsing(false);
          },
          error: () => setParsing(false)
        });
      } else if (ext === 'xlsx' || ext === 'xls') {
        // Excel parsing with SheetJS
        const reader = new FileReader();
        reader.onload = (ev) => {
          try {
            const wb = XLSX.read(ev.target.result, { type: 'array' });
            const sheetName = wb.SheetNames[0];
            const sheet = wb.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });
            const hdrs = jsonData.length > 0 ? Object.keys(jsonData[0]) : [];
            processHeadersAndData(hdrs, jsonData);
          } catch {}
          setParsing(false);
        };
        reader.readAsArrayBuffer(file);
      } else if (ext === 'pdf') {
        // PDF parsing via backend
        try {
          const { data } = await statementApi.parsePdf(file);
          processHeadersAndData(data.headers || [], data.rows || []);
        } catch {}
        setParsing(false);
      } else {
        setParsing(false);
      }
    } catch {
      setParsing(false);
    }
  };

  const handleParse = () => {
    if (!dateCol) return;
    const rows = rawData.map((row, idx) => {
      const date = parseDate(row[dateCol]);
      const debitAmt = debitCol ? parseAmount(row[debitCol]) : 0;
      const creditAmt = creditCol ? parseAmount(row[creditCol]) : 0;
      const desc = descCol ? (String(row[descCol] || '')).trim() : '';

      let entryType = 'expense';
      let amount = 0;
      if (debitAmt > 0 && creditAmt === 0) {
        entryType = 'expense'; amount = debitAmt;
      } else if (creditAmt > 0 && debitAmt === 0) {
        entryType = 'income'; amount = creditAmt;
      } else if (debitAmt > 0) {
        entryType = 'expense'; amount = debitAmt;
      } else if (creditAmt > 0) {
        entryType = 'income'; amount = creditAmt;
      }
      if (!creditCol && debitAmt > 0) {
        entryType = 'expense'; amount = debitAmt;
      }

      return {
        idx: idx + 1, date, entryType, amount, notes: desc,
        entityId: '', entityName: '', bankAccountId: '', bankAccountName: '',
        skip: amount === 0 || !date,
      };
    }).filter(r => !r.skip);
    setParsedRows(rows);
    setStep('review');
  };

  const applyBulkEntity = () => {
    if (!bulkEntityId) return;
    setParsedRows(prev => prev.map(r => ({
      ...r, entityId: bulkEntityId, entityName: bulkEntityName,
      bankAccountId: bulkBankAccountId, bankAccountName: bulkBankAccountName,
    })));
  };

  const autoSuggestEntities = async () => {
    setSuggesting(true);
    try {
      const updated = [...parsedRows];
      for (let i = 0; i < updated.length; i++) {
        const row = updated[i];
        if (row.entityId) continue; // Skip already assigned
        if (!row.notes || row.notes.length < 3) continue;
        try {
          const { data } = await statementApi.suggestEntity(row.notes, category);
          if (data && data.length > 0 && data[0].score >= 0.3) {
            updated[i] = {
              ...row,
              entityId: data[0].entity_id,
              entityName: data[0].entity_name,
              bankAccountId: data[0].bank_account_id || '',
              bankAccountName: data[0].bank_account_name || '',
              _suggested: true,
              _source: data[0].source || 'fuzzy',
            };
          }
        } catch {}
      }
      setParsedRows(updated);
    } catch {}
    setSuggesting(false);
  };

  const updateParsedRow = (idx, field, value) => {
    setParsedRows(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const updated = { ...r, [field]: value };
      if (field === 'entityId') {
        const ent = entities.find(e => e._id === value);
        updated.entityName = ent?.name || '';
        updated.bankAccountId = '';
        updated.bankAccountName = '';
        if (fetchEntityBankAccounts) fetchEntityBankAccounts(value);
      }
      if (field === 'bankAccountId') {
        const acc = entityBankAccounts.find(a => a._id === value);
        updated.bankAccountName = acc ? `${acc.account_name}${acc.bank_name ? ' - ' + acc.bank_name : ''}` : '';
      }
      return updated;
    }));
  };

  const toggleRowType = (idx) => {
    setParsedRows(prev => prev.map((r, i) =>
      i === idx ? { ...r, entryType: r.entryType === 'expense' ? 'income' : 'expense' } : r
    ));
  };

  const handleSubmit = async () => {
    const validRows = parsedRows.filter(r => r.date && r.amount > 0);
    if (validRows.length === 0 || !siteId) return;
    setSubmitting(true);
    try {
      const payload = validRows.map(r => ({
        date: r.date, site_id: siteId, entry_type: r.entryType, category,
        sub_category: subCategory,
        entity_id: r.entityId || bulkEntityId || '',
        entity_name: r.entityName || bulkEntityName || '',
        payment_mode: 'bank',
        bank_account_id: r.bankAccountId || bulkBankAccountId || '',
        bank_account_name: r.bankAccountName || bulkBankAccountName || '',
        amount: String(r.amount), notes: r.notes
      }));
      await entriesApi.bulkUpload(payload);

      // Auto-learn: collect rows where user manually assigned entities (not auto-suggested)
      const manualMappings = validRows
        .filter(r => r.entityId && r.notes && r.notes.length >= 3 && !r._suggested)
        .map(r => ({
          description: r.notes,
          entity_id: r.entityId,
          entity_name: r.entityName,
          bank_account_id: r.bankAccountId || '',
          bank_account_name: r.bankAccountName || '',
        }));
      if (manualMappings.length > 0) {
        try { await statementApi.learnMappings(manualMappings); } catch {}
      }

      onSuccess?.();
      onOpenChange(false);
      resetAll();
    } catch {}
    setSubmitting(false);
  };

  const resetAll = () => {
    setStep('upload'); setRawData([]); setHeaders([]); setDetectedFormat(null); setFileType('');
    setDateCol(''); setDebitCol(''); setCreditCol(''); setDescCol('');
    setParsedRows([]); setParsing(false);
  };

  const expenseTotal = parsedRows.filter(r => r.entryType === 'expense').reduce((s, r) => s + r.amount, 0);
  const incomeTotal = parsedRows.filter(r => r.entryType === 'income').reduce((s, r) => s + r.amount, 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetAll(); onOpenChange(v); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" /> Import Bank Statement</DialogTitle>
          <DialogDescription>Upload a CSV, Excel, or PDF bank statement. We'll auto-detect the format.</DialogDescription>
        </DialogHeader>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="space-y-4" data-testid="stmt-upload-step">
            <div className="border-2 border-dashed border-border p-8 text-center">
              {parsing ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-10 h-10 animate-spin text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Parsing file...</p>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Upload Bank Statement</Label>
                  <Input type="file" accept=".csv,.xlsx,.xls,.pdf" onChange={handleFileUpload} className="mt-3 max-w-sm mx-auto" data-testid="stmt-file-input" />
                  <p className="text-xs text-muted-foreground mt-2">Supports CSV, Excel (.xlsx/.xls), and PDF formats</p>
                  <p className="text-xs text-muted-foreground">Auto-detects SBI, HDFC, ICICI, Axis & generic formats</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Column Mapping */}
        {step === 'map' && (
          <div className="space-y-4" data-testid="stmt-map-step">
            {detectedFormat && (
              <div className="flex items-center gap-2 text-sm bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 p-3">
                <Check className="w-4 h-4 text-emerald-600" />
                <span>Auto-detected <strong>{detectedFormat.name}</strong> format from {fileType.toUpperCase()} file</span>
              </div>
            )}
            {!detectedFormat && (
              <div className="flex items-center gap-2 text-sm bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 p-3">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <span>Could not auto-detect format. Please map columns manually.</span>
              </div>
            )}
            <div className="text-xs text-muted-foreground">{rawData.length} rows, {headers.length} columns: {headers.slice(0, 8).join(', ')}{headers.length > 8 ? '...' : ''}</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground font-semibold">Date Column *</Label>
                <Select value={dateCol} onValueChange={setDateCol}>
                  <SelectTrigger className="mt-1 h-8 text-xs" data-testid="stmt-date-col"><SelectValue placeholder="Select column" /></SelectTrigger>
                  <SelectContent>{headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground font-semibold">Debit / Withdrawal Column</Label>
                <Select value={debitCol || '__none__'} onValueChange={v => setDebitCol(v === '__none__' ? '' : v)}>
                  <SelectTrigger className="mt-1 h-8 text-xs" data-testid="stmt-debit-col"><SelectValue placeholder="Select column" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- None --</SelectItem>
                    {headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground font-semibold">Credit / Deposit Column</Label>
                <Select value={creditCol || '__none__'} onValueChange={v => setCreditCol(v === '__none__' ? '' : v)}>
                  <SelectTrigger className="mt-1 h-8 text-xs" data-testid="stmt-credit-col"><SelectValue placeholder="Select column" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- None --</SelectItem>
                    {headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground font-semibold">Particulars / Description Column</Label>
                <Select value={descCol || '__none__'} onValueChange={v => setDescCol(v === '__none__' ? '' : v)}>
                  <SelectTrigger className="mt-1 h-8 text-xs" data-testid="stmt-desc-col"><SelectValue placeholder="Select column" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- None --</SelectItem>
                    {headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setStep('upload'); setDetectedFormat(null); }}>Back</Button>
              <Button onClick={handleParse} disabled={!dateCol} data-testid="stmt-parse-btn">Parse Transactions</Button>
            </div>
          </div>
        )}

        {/* Step 3: Review & Assign */}
        {step === 'review' && (
          <div className="space-y-4" data-testid="stmt-review-step">
            <div className="bg-muted/50 border border-border p-3 space-y-3">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Assignment</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Site *</Label>
                  <Select value={siteId} onValueChange={setSiteId}>
                    <SelectTrigger className="mt-1 h-8 text-xs" data-testid="stmt-site"><SelectValue placeholder="Select site" /></SelectTrigger>
                    <SelectContent>{sites.map(s => <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Category</Label>
                  <Select value={category} onValueChange={v => { setCategory(v); setBulkEntityId(''); setBulkEntityName(''); }}>
                    <SelectTrigger className="mt-1 h-8 text-xs" data-testid="stmt-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.filter(c => c.value !== 'internal_transfer').map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Bulk Assign Entity</Label>
                  <Select value={bulkEntityId || '__none__'} onValueChange={v => {
                    if (v === '__none__') { setBulkEntityId(''); setBulkEntityName(''); return; }
                    const ent = entities.find(e => e._id === v);
                    setBulkEntityId(v); setBulkEntityName(ent?.name || '');
                    if (fetchEntityBankAccounts) fetchEntityBankAccounts(v);
                  }}>
                    <SelectTrigger className="mt-1 h-8 text-xs" data-testid="stmt-bulk-entity"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- Per row --</SelectItem>
                      {catEntities.map(e => <SelectItem key={e._id} value={e._id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {bulkEntityId && entityBankAccounts.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Bulk Bank Account</Label>
                    <Select value={bulkBankAccountId || '__none__'} onValueChange={v => {
                      if (v === '__none__') { setBulkBankAccountId(''); setBulkBankAccountName(''); return; }
                      const acc = entityBankAccounts.find(a => a._id === v);
                      setBulkBankAccountId(v);
                      setBulkBankAccountName(acc ? `${acc.account_name}${acc.bank_name ? ' - ' + acc.bank_name : ''}` : '');
                    }}>
                      <SelectTrigger className="mt-1 h-8 text-xs" data-testid="stmt-bulk-bank"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- None --</SelectItem>
                        {entityBankAccounts.map(a => <SelectItem key={a._id} value={a._id}>{a.account_name}{a.bank_name ? ` (${a.bank_name})` : ''}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {bulkEntityId && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={applyBulkEntity} data-testid="stmt-apply-bulk">Apply to All Rows</Button>
              )}
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={autoSuggestEntities} disabled={suggesting} data-testid="stmt-auto-suggest">
                {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {suggesting ? 'Matching...' : 'Auto-Suggest Entities'}
              </Button>
            </div>

            <div className="flex gap-4 text-xs">
              <span>{parsedRows.length} transactions</span>
              <span className="text-red-600 font-mono">Out: {'\u20B9'}{expenseTotal.toLocaleString('en-IN')}</span>
              <span className="text-emerald-600 font-mono">In: {'\u20B9'}{incomeTotal.toLocaleString('en-IN')}</span>
            </div>

            <div className="border border-border overflow-x-auto max-h-80">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted z-10">
                  <tr className="border-b border-border">
                    <th className="py-2 px-2 text-left font-semibold text-muted-foreground w-8">#</th>
                    <th className="py-2 px-2 text-left font-semibold text-muted-foreground w-24">Date</th>
                    <th className="py-2 px-2 text-left font-semibold text-muted-foreground w-16">Type</th>
                    <th className="py-2 px-2 text-right font-semibold text-muted-foreground w-24">Amount</th>
                    <th className="py-2 px-2 text-left font-semibold text-muted-foreground">Particulars</th>
                    <th className="py-2 px-2 text-left font-semibold text-muted-foreground w-36">Entity (override)</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedRows.map((row, idx) => (
                    <tr key={idx} className="border-b border-border hover:bg-muted/30" data-testid={`stmt-row-${idx}`}>
                      <td className="py-1.5 px-2 text-muted-foreground">{row.idx}</td>
                      <td className="py-1.5 px-2">{row.date}</td>
                      <td className="py-1.5 px-2">
                        <button onClick={() => toggleRowType(idx)}
                          className={`text-xs px-1.5 py-0.5 cursor-pointer ${row.entryType === 'income' ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400'}`}
                          data-testid={`stmt-toggle-type-${idx}`}>
                          {row.entryType === 'income' ? 'IN' : 'OUT'}
                        </button>
                      </td>
                      <td className={`py-1.5 px-2 text-right font-mono ${row.entryType === 'income' ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                        {'\u20B9'}{row.amount.toLocaleString('en-IN')}
                      </td>
                      <td className="py-1.5 px-2 max-w-[200px] truncate" title={row.notes}>{row.notes || '-'}</td>
                      <td className="py-1.5 px-2">
                        <div className="flex items-center gap-1">
                          {row._suggested && row._source === 'learned' && <BookOpen className="w-3 h-3 text-blue-500 shrink-0" title="Matched from learned history" />}
                          {row._suggested && row._source !== 'learned' && <Sparkles className="w-3 h-3 text-amber-500 shrink-0" title="Auto-suggested (fuzzy match)" />}
                          <Select value={row.entityId || '__none__'} onValueChange={v => updateParsedRow(idx, 'entityId', v === '__none__' ? '' : v)}>
                            <SelectTrigger className="h-6 text-xs border-0 bg-transparent px-1"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">-- Use bulk --</SelectItem>
                              {catEntities.map(e => <SelectItem key={e._id} value={e._id}>{e.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" onClick={() => setStep('map')}>Back to Mapping</Button>
              <Button onClick={handleSubmit} disabled={submitting || parsedRows.length === 0 || !siteId} data-testid="stmt-submit">
                {submitting ? 'Importing...' : `Import ${parsedRows.length} Transactions`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
